<script setup lang="ts">
import { tpgIconClose } from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarBadge,
  GrammarFooter,
  GrammarOverlayCard,
  GrammarPrimaryButton,
  GrammarQuaternaryButton,
  GrammarWorkflowToast,
} from '@nora-monorepo/grammar-ui';

import RouteList from '#nora-ui/map/components/drawtools/route-tool/RouteList.vue';
import RouteToolAddressSearch from '#nora-ui/map/components/drawtools/route-tool/RouteToolAddressSearch.vue';
import RouteToolEmptyState from '#nora-ui/map/components/drawtools/route-tool/RouteToolEmptyState.vue';
import type { RouteTool } from '#nora-ui/map/drawtools/route-tool';
import { RouteViewMode } from '#nora-ui/map/drawtools/stores/route-tool-store';

interface Props {
  tool: RouteTool;
  mapId: string;
}
const props = defineProps<Props>();
const { t } = useI18n();
const { tool } = toRefs(props);
const routeToolStore = tool.value.routeToolStore;
const { routeCoordinates, viewMode, isValidRoute, currentError, errorMessage } = storeToRefs(routeToolStore);

const canSaveRoute = computed(() => {
  return isValidRoute.value;
});

async function retryRoute() {
  await tool.value.handleRouteChange();
}
</script>

<template>
  <GrammarOverlayCard
    label="Route"
    class="max-h-[70vh] max-w-[375px] h-full"
  >
    <template #header-trailing-buttons>
      <GrammarQuaternaryButton
        :icon-template="tpgIconClose"
        @click="tool.onCloseButtonPressed"
      />
    </template>
    <RouteToolAddressSearch
      :tool="tool"
      :map-id="mapId"
    />
    <RouteToolEmptyState
      v-if="routeCoordinates.length < 2"
      :coordinate-count="routeCoordinates.length"
    />
    <RouteList
      v-if="routeToolStore.viewMode === RouteViewMode.PointList"
      :tool="tool"
      :route-error="currentError"
    />
    <template #footer>
      <GrammarFooter
        v-if="currentError && viewMode === RouteViewMode.PointList"
        slot-override
      >
        <template #override-bottom-content>
          <GrammarWorkflowToast
            is-open
          >
            <template #leading-badge>
              <GrammarBadge
                full-width
                type="error"
                :label="errorMessage?.message"
                :bottom-label="errorMessage?.detail"
                emphasis="no-background-high"
              />
            </template>
            <template
              v-if="currentError.showRetryButton"
              #trailing-badge
            >
              <GrammarQuaternaryButton
                size="small"
                label="Retry"
                @click="retryRoute"
              />
            </template>
          </GrammarWorkflowToast>
        </template>
      </GrammarFooter>

      <GrammarFooter v-else>
        <template #right-button>
          <GrammarPrimaryButton
            v-if="viewMode === RouteViewMode.PointList"
            :disabled="canSaveRoute === false"
            size="regular"
            :label="t('drawTools.route.saveRouteButton')"
            @click="tool.onSaveButtonPressed"
          />
        </template>
      </GrammarFooter>
    </template>
  </GrammarOverlayCard>
</template>
